// Visibility rules for the phone trial bar (components/PhoneStickyCta.vue), kept free of Vue and the
// DOM so tests/tracking/phoneStickyCta.test.ts can check them. The component wires these to its
// IntersectionObservers and the cookie banner state.

type ElementLike = {
  matches(selector: string): boolean;
  querySelector(selector: string): ElementLike | null;
  firstElementChild: ElementLike | null;
};

export type HeroTarget<E> = { el: E; byButton: boolean } | null;

// The bar shows only while the hero's buttons are off screen, no section with its own buttons (or the
// footer) is on screen, and the cookie banner doesn't need an answer.
export function stickyVisible(state: { heroOnScreen: boolean; overOwnCta: boolean; needsBanner: boolean }): boolean {
  return !state.heroOnScreen && !state.overOwnCta && !state.needsBanner;
}

// What to observe for "the hero's buttons are on screen".
// A row set to display: contents (the phone hero reorder) has no box, so an observer on it would never
// fire and the bar would show over the hero: use its first child instead. Then prefer the hero's own
// tracked button, so a button sitting right at the fold (a 3px sliver) doesn't keep the bar off.
export function heroTarget<E extends ElementLike>(hero: E | null, display: (el: E) => string): HeroTarget<E> {
  let row = hero;
  if (row && display(row) === "contents") row = row.firstElementChild as E | null;
  if (!row) return null;
  const button = row.matches("a[data-conversion-event]")
    ? row
    : (row.querySelector("a[data-conversion-event]") as E | null);
  return button ? { el: button, byButton: true } : { el: row, byButton: false };
}

// A tracked button counts as on screen only while half of it is visible; a plain row counts as soon
// as any of it is.
export function heroOnScreenFrom(entry: { intersectionRatio: number; isIntersecting: boolean }, byButton: boolean): boolean {
  return byButton ? entry.intersectionRatio >= 0.5 : entry.isIntersecting;
}

export const HERO_BUTTON_THRESHOLDS = [0, 0.5, 1];

// Tracks which "hide" sections are on screen; returns true while any of them is.
export function createHideTracker<T>() {
  const showing = new Set<T>();
  return (entries: Iterable<{ target: T; isIntersecting: boolean }>): boolean => {
    for (const entry of entries) {
      if (entry.isIntersecting) showing.add(entry.target);
      else showing.delete(entry.target);
    }
    return showing.size > 0;
  };
}
